/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon, DefaultButton, PrimaryButton, Dialog, DialogType, DialogFooter, Spinner, SpinnerSize } from '@fluentui/react';
import { AUTH_CONFIG } from '../../Auth/auth0-variables';
import axios from 'axios';
import moment from 'moment';
import ShiftsContext from '../../Context/ShiftsContext';
import TrueFalse from './Quiz/TrueFalse';
import SingleSelect from './Quiz/SingleSelect';
import FreeAnswer from './Quiz/FreeAnswer';
import MultipleSelect from './Quiz/MultipleSelect';
import Complete from './Quiz/Complete';
import StarRating from './Quiz/StarRating';
import Text from './Quiz/Text';
import Video from './Quiz/Video';
import ReviewStep from './Quiz/ReviewStep';

/* ═══════════════════════════════════════════
   STYLES
   ═══════════════════════════════════════════ */

const pageContainer = css`
    display: flex;
    overflow: hidden;
    background-color: var(--colorNeutralBackground2);
    height: 100%;
`;

const sidebarStyle = (collapsed) => css`
    width: ${collapsed ? '0px' : '340px'};
    min-width: ${collapsed ? '0px' : '340px'};
    transition: width 0.3s ease, min-width 0.3s ease;
    background-color: rgba(var(--palette-neutral-0), 1);
    border-right: 1px solid rgba(var(--palette-neutral-4), 1);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
`;

const sidebarInner = css`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 340px;
    overflow: hidden;
`;

const expandToggle = css`
    position: absolute;
    left: 12px;
    top: 12px;
    z-index: 10;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(var(--palette-neutral-0), 1);
    border: 1px solid rgba(var(--palette-neutral-4), 1);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0,0,0,.12);
    &:hover { background: rgba(var(--palette-neutral-2), 1); }
`;

const toggleGroup = css`
    display: flex;
    gap: 0;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.15);
    margin: 16px 16px 0;
    flex-shrink: 0;
`;

const toggleBtn = css`
    flex: 1;
    padding: 5px 16px;
    font-size: 13px;
    font-weight: 500;
    border: none;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
    background: transparent;
    color: rgba(0, 0, 0, 0.5);
    &:hover {
        background: rgba(0, 0, 0, 0.04);
    }
`;

const toggleBtnActive = css`
    background: var(--themePrimary2, #0078d4) !important;
    color: #fff !important;
    &:hover {
        background: var(--themePrimary2, #0078d4) !important;
    }
`;

const courseListContainer = css`
    flex: 1;
    overflow-y: auto;
    padding: 8px 16px 16px;
`;

const cardBox = (isSelected, clickable = true) => css`
    box-shadow: ${isSelected ? '0 0 0 2px #008272' : '0 1px 2px rgba(0,0,0,.12)'};
    border-radius: 6px;
    background-color: rgba(var(--palette-neutral-0), 1);
    overflow: hidden;
    cursor: ${clickable ? 'pointer' : 'default'};
    margin-bottom: 12px;
    transition: box-shadow 0.15s;
    ${clickable ? `&:hover { box-shadow: ${isSelected ? '0 0 0 2px #008272' : '0 1px 4px rgba(0,0,0,.22)'}; }` : ''}
`;

const cardTop = css`
    background-color: #daedeb;
    padding: 12px 15px;
    position: relative;
`;

const cardCategory = css`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    margin: 0 0 4px;
    line-height: 1.2;
`;

const cardTitle = css`
    font-size: 13px;
    margin: 0;
`;

const cardPill = css`
    border-radius: 10px;
    background-color: #008272;
    position: absolute;
    right: 8px;
    top: 8px;
    padding: 1px 10px;
    font-size: 11px;
    color: #fff;
`;

const cardBody = css`
    padding: 10px 12px;
    display: flex;
    align-items: flex-start;
`;

const cardDescription = css`
    font-size: 12px;
    color: #605e5c;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    margin: 0;
`;

const cardDueDate = css`
    font-size: 11px;
    color: brown;
    margin-top: 4px;
`;

const cardScore = css`
    font-size: 12px;
    color: #008272;
    font-weight: 600;
    margin-top: 4px;
`;

const cardCompleted = css`
    font-size: 11px;
    color: #605e5c;
    margin-top: 2px;
`;

const retakeLink = css`
    font-size: 12px;
    color: #008272;
    cursor: pointer;
    font-weight: 600;
    margin-top: 4px;
    &:hover { text-decoration: underline; }
`;

const quizSection = css`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    overflow-y: auto;
    padding: 0;
    position: relative;
    background-color: var(--colorNeutralBackground2);
`;

const quizInner = css`
    width: 100%;
    max-width: 560px;
    display: flex;
    flex-direction: column;
    padding: 24px;
        height: 75vh;
    min-height: 600px;
`;

const progressBarTrack = css`
    width: 100%;
    height: 10px;
    background: rgba(var(--palette-neutral-4), 1);
    border-radius: 5px;
    overflow: hidden;
    margin-bottom: 4px;
`;

const progressBarFill = (pct) => css`
    height: 100%;
    width: ${pct * 100}%;
    background: #2583d5;
    border-radius: 5px;
    transition: width 0.35s ease;
`;

const stepCount = css`
    font-size: 11px;
    text-align: right;
    color: #605e5c;
    margin-bottom: 20px;
`;

const questionArea = css`
    position: relative;
    overflow: hidden;
    flex: 1;
    min-height: 300px;
`;

const navBar = css`
    position: fixed;
    bottom: 0;
    right: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 24px;
    background: rgba(var(--palette-neutral-0), 1);
    box-shadow: 0 -2px 10px rgba(0,0,0,.12);
    z-index: 10;
`;

const emptyState = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
    color: #605e5c;
    flex: 1;
    img { width: 120px; margin-bottom: 16px; opacity: 0.6; }
`;

const selectPrompt = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    color: #a19f9d;
    font-size: 16px;
    text-align: center;
    padding: 40px;
    img { width: 150px; margin-bottom: 20px; opacity: 0.5; }
`;

/* ═══════════════════════════════════════════
   MAIN PAGE COMPONENT
   ═══════════════════════════════════════════ */

function QuizzesPage() {
    const { getUnfinishedCourses } = useContext(ShiftsContext);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [activeTab, setActiveTab] = useState(0); // 0=Incomplete, 1=Complete
    const [incomplete, setIncomplete] = useState([]);
    const [completed, setCompleted] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [selectedCourseTitle, setSelectedCourseTitle] = useState('');
    const [retakeDialogOpen, setRetakeDialogOpen] = useState(false);
    const [retakeCourseId, setRetakeCourseId] = useState(null);

    const fetchCourses = useCallback(() => {
        setLoading(true);
        Promise.all([
            axios.get(AUTH_CONFIG.API_URL + '/api/course/GetUnfinishedCourses'),
            axios.get(AUTH_CONFIG.API_URL + '/api/Mobile/GetCompletedCourses'),
        ])
            .then(([incRes, compRes]) => {
                setIncomplete(Array.isArray(incRes.data) ? incRes.data : []);
                setCompleted(Array.isArray(compRes.data) ? compRes.data : []);
            })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchCourses();
    }, [fetchCourses]);

    const handleSelectCourse = (courseId, title) => {
        setSelectedCourseId(courseId);
        setSelectedCourseTitle(title || '');
    };

    const handleQuizComplete = () => {
        setSelectedCourseId(null);
        fetchCourses();
        getUnfinishedCourses();
    };

    const handleRetakeRequest = (userCourseId) => {
        setRetakeCourseId(userCourseId);
        setRetakeDialogOpen(true);
    };

    const handleRetakeConfirm = () => {
        if (!retakeCourseId) return;
        axios.post(AUTH_CONFIG.API_URL + `/api/Mobile/RetakeCourse?userCourseId=${retakeCourseId}`)
            .then(() => {
                setRetakeDialogOpen(false);
                setSelectedCourseId(retakeCourseId);
                const course = completed.find(c => (c.UserCourseId || c.userCourseId) === retakeCourseId);
                setSelectedCourseTitle(course?.Title || course?.title || '');
                setActiveTab(0);
                fetchCourses();
                getUnfinishedCourses();
            })
            .catch(() => setRetakeDialogOpen(false));
    };

    const currentList = activeTab === 0 ? incomplete : completed;

    return (
        <div className="layout__row" css={pageContainer}>
            {/* Sidebar */}
            <div className="custom-scrollbar" css={sidebarStyle(sidebarCollapsed)}>
                {!sidebarCollapsed && (
                    <div css={sidebarInner}>
                        <div css={toggleGroup}>
                            <button css={[toggleBtn, activeTab === 0 && toggleBtnActive]} onClick={() => setActiveTab(0)}>
                                Incomplete
                            </button>
                            <button css={[toggleBtn, activeTab === 1 && toggleBtnActive]} onClick={() => setActiveTab(1)}>
                                Complete
                            </button>
                        </div>
                        <div css={courseListContainer}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: 40 }}>
                                    <Spinner size={SpinnerSize.medium} />
                                </div>
                            ) : currentList.length === 0 ? (
                                <div css={emptyState}>
                                    <img src="https://teamsly-cdn-cuc0cvgwakbtarf9.z01.azurefd.net/images/trophy.png" alt="" />
                                    <p>{activeTab === 0 ? 'No incomplete quizzes or lessons' : 'No completed quizzes or lessons'}</p>
                                </div>
                            ) : (
                                currentList.map((item) => {
                                    const id = item.UserCourseId || item.userCourseId;
                                    return activeTab === 0
                                        ? <IncompleteCourseCard
                                            key={id}
                                            data={item}
                                            isSelected={selectedCourseId === id}
                                            onClick={() => handleSelectCourse(id, item.Title || item.title)}
                                        />
                                        : <CompleteCourseCard
                                            key={id}
                                            data={item}
                                            isSelected={selectedCourseId === id}
                                            onClick={() => handleSelectCourse(id, item.Title || item.title)}
                                            onRetake={() => handleRetakeRequest(id)}
                                        />;
                                })
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Quiz Section */}
            <div css={quizSection}>
                {/* Sidebar toggle inside quiz section */}
                <div css={expandToggle} onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
                    <Icon iconName={sidebarCollapsed ? "DoubleChevronRight" : "DoubleChevronLeft"} style={{ fontSize: 12 }} />
                </div>
                {selectedCourseId ? (
                    <QuizPlayer
                        key={selectedCourseId}
                        userCourseId={selectedCourseId}
                        title={selectedCourseTitle}
                        onComplete={handleQuizComplete}
                    />
                ) : (
                    <div css={selectPrompt}>
                        <img src="https://teamsly-cdn-cuc0cvgwakbtarf9.z01.azurefd.net/images/trophy.png" alt="" />
                        <p>Select a quiz or lesson from the sidebar to get started</p>
                    </div>
                )}
            </div>

            {/* Retake confirmation dialog */}
            <Dialog
                hidden={!retakeDialogOpen}
                onDismiss={() => setRetakeDialogOpen(false)}
                modalProps={{ layerProps: { styles: { root: { zIndex: 10000001 } } } }}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Retake Course',
                    subText: 'Are you sure you want to retake this course? Your previous answers will be cleared.',
                }}
            >
                <DialogFooter>
                    <PrimaryButton onClick={handleRetakeConfirm} text="Retake" />
                    <DefaultButton onClick={() => setRetakeDialogOpen(false)} text="Cancel" />
                </DialogFooter>
            </Dialog>
        </div>
    );
}

/* ═══════════════════════════════════════════
   INCOMPLETE COURSE CARD
   ═══════════════════════════════════════════ */

function IncompleteCourseCard({ data, isSelected, onClick }) {
    const title = data.Title || data.title || '';
    const category = data.Category || data.category || '';
    const type = data.Type || data.type || '';
    const desc = data.ShortDescription || data.shortDescription || '';
    const dueDate = data.DueDate || data.dueDate;

    return (
        <div className="box2" css={cardBox(isSelected)} onClick={onClick}>
            <div css={cardTop}>
                <p css={cardCategory}>{category}</p>
                <p css={cardTitle}>{title}</p>
                <span css={cardPill}>{type}</span>
            </div>
            <div css={cardBody}>
                <div>
                    <p css={cardDescription}>{desc}</p>
                    {dueDate && (
                        <div css={cardDueDate}>
                            Complete by: {moment(dueDate).format("MMM Do · h:mma").replace('am', 'a').replace('pm', 'p')}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════
   COMPLETE COURSE CARD
   ═══════════════════════════════════════════ */

function CompleteCourseCard({ data, isSelected, onClick, onRetake }) {
    const title = data.Title || data.title || '';
    const category = data.Category || data.category || '';
    const type = data.Type || data.type || '';
    const desc = data.ShortDescription || data.shortDescription || '';
    const completedOn = data.CompletedOn || data.completedOn;
    const score = data.Score ?? data.score;
    const retakes = data.Retakes ?? data.retakes;
    const canRetake = !!retakes;

    return (
        <div css={cardBox(isSelected, canRetake)} onClick={canRetake ? () => onRetake() : undefined}>
            <div css={cardTop}>
                <p css={cardCategory}>{category}</p>
                <p css={cardTitle}>{title}</p>
                <span css={cardPill}>{type}</span>
            </div>
            <div css={cardBody}>
                <div style={{ flex: 1 }}>
                    {score != null && (
                        <div css={cardScore}>Score: {score}%</div>
                    )}
                    {completedOn && (
                        <div css={cardCompleted}>
                            Completed: {moment(completedOn).format('MMM D, YYYY')}
                        </div>
                    )}
                    {canRetake && (
                        <div css={retakeLink}>
                            Retake
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════
   QUIZ PLAYER — redesigned with proper animations
   ═══════════════════════════════════════════ */

const slideVariants = {
    enter: (dir) => ({
        x: dir === 'next' ? 600 : -600,
        opacity: 0,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
    }),
    center: {
        x: 0,
        opacity: 1,
        position: 'relative',
    },
    exit: (dir) => ({
        x: dir === 'next' ? -600 : 600,
        opacity: 0,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
    }),
};

const slideTransition = {
    x: { type: 'spring', stiffness: 300, damping: 30 },
    opacity: { duration: 0.2 },
};

function QuizPlayer({ userCourseId, title, onComplete }) {
    const { getUnfinishedCourses } = useContext(ShiftsContext);
    const [steps, setSteps] = useState([]);
    const [step, setStep] = useState({});
    const [complete, setComplete] = useState(false);
    const [value, setValue] = useState();
    const [valueId, setValueId] = useState();
    const [values, setValues] = useState([]);
    const [stepDisabled, setStepDisabled] = useState(true);
    const [score, setScore] = useState();
    const [direction, setDirection] = useState('next');
    const [showExplanation, setShowExplanation] = useState(false);
    const [showCorrect, setShowCorrect] = useState(false);
    const [showInCorrect, setShowIncorrect] = useState(false);
    const [explanationCorrect, setExplanationCorrect] = useState(null);
    const [answeredQuestions, setAnsweredQuestions] = useState({});
    const [justAnsweredId, setJustAnsweredId] = useState(null);
    const feedbackTimerRef = useRef(null);
    const feedbackCompleteRef = useRef(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getStep(1, 0, 0);
    }, [userCourseId]);

    const setStepValue = (s) => {
        if (s && s.userAnswers && s.userAnswers.length > 0) {
            if (s.slug === 'multi-select') {
                setValues(s.userAnswers.map(y => y.qnsAnswerId));
            } else if (s.slug === 'single-select' || s.slug === 'true-false') {
                setValueId(s.userAnswers[0].qnsAnswerId);
            } else if (s.slug === 'free-answer' || s.slug === 'rating') {
                setValue(s.userAnswers[0].value);
            }
        }
        if (s && (s.slug === 'text' || s.slug === 'video')) {
            setStepDisabled(false);
        }
    };

    const getStep = (whichstep, retake, takeagain) => {
        axios.get(AUTH_CONFIG.API_URL + `/api/course/GetCourseCourses?userCourseId=${userCourseId}&whichStep=${whichstep}&retake=${retake}&takeagain=${takeagain}`)
            .then((response) => {
                const data = response.data;
                const remaining = data.filter(x => !x.stepComplete).length;
                if (!remaining) {
                    setComplete(true);
                    getUnfinishedCourses();
                } else {
                    const active = data.filter(x => x.isActive)[0];
                    if (active) {
                        setStepValue(active);
                        setStep(active);
                    } else {
                        const next = data.filter(x => !x.stepComplete)[0];
                        setStep(next);
                    }
                }
                setSteps(data);
                setLoading(false);
            });
    };

    const reset = () => {
        setValue(undefined);
        setValueId(undefined);
        setValues([]);
        setStepDisabled(true);
    };

    const setNextstep = (comp) => {
        setDirection('next');
        reset();
        if (comp || step.stepNumber === steps.length) {
            step.courseStepId = -9;
            setStep({ ...step });
            setComplete(true);
        } else {
            const next = steps.filter(x => x.stepNumber === step.stepNumber + 1)[0];
            if (next) {
                setStepValue(next);
                setStep(next);
            }
        }
    };

    const setstepBack = () => {
        if (direction === 'back') {
            doBack();
        } else {
            setDirection('back');
        }
    };

    const doBack = () => {
        setDirection('back');
        reset();
        setComplete(false);
        setJustAnsweredId(null);
        const prev = steps.filter(x => x.stepNumber === step.stepNumber - 1)[0];
        if (prev) {
            setStepValue(prev);
            setStep(prev);
        }
    };

    useEffect(() => {
        if (direction === 'back') {
            doBack();
        }
    }, [direction]);

    const showFeedback = (isCorrect, d) => {
        // The two settings are independent: showCorrect reveals the grade,
        // showExplanation reveals the explanation (with or without the grade).
        const revealGrade = !!step.showCorrect;
        const revealExplanation = !!step.showExplanation && !!step.explanation;

        if (!revealGrade && !revealExplanation) {
            setNextstep(d);
            return;
        }

        feedbackCompleteRef.current = d;

        if (revealExplanation) {
            setExplanationCorrect(revealGrade ? isCorrect : null);
            setShowExplanation(true);
            return;
        }

        if (isCorrect) {
            setShowCorrect(true);
            feedbackTimerRef.current = setTimeout(() => {
                setShowCorrect(false);
                setNextstep(d);
            }, 1500);
        } else {
            setShowIncorrect(true);
            feedbackTimerRef.current = setTimeout(() => {
                setShowIncorrect(false);
                setNextstep(d);
            }, 1500);
        }
    };

    function areEqual(a1, a2) {
        if (a1.length !== a2.length) return false;
        return a1.every(el => a2.includes(el));
    }

    const computeCorrect = (s, selectedIds) => {
        if (s.slug === 'single-select' || s.slug === 'true-false') {
            const a = s.answers.find(x => selectedIds.includes(x.qnsAnswerId));
            return a?.isCorrect || false;
        }
        if (s.slug === 'multi-select') {
            const correctIds = s.answers.filter(x => x.isCorrect).map(x => x.qnsAnswerId);
            if (s.everyCorrectSelected === 'one') {
                return selectedIds.some(v => correctIds.includes(v));
            }
            return areEqual(selectedIds, correctIds);
        }
        return null;
    };

    // Returns review data ({ selectedIds, value, correct }) when the step was
    // already answered (this session or a previous one), else null.
    const getReviewData = (s) => {
        if (!s) return null;
        if (s.slug === 'text' || s.slug === 'video') return null;
        if (s.userCourseStepId === justAnsweredId) return null;
        const local = answeredQuestions[s.userCourseStepId];
        if (local) return local;
        if (s.userAnswers && s.userAnswers.length > 0) {
            const selectedIds = s.userAnswers.map(y => y.qnsAnswerId).filter(x => x != null);
            const val = s.userAnswers[0].value != null ? s.userAnswers[0].value : null;
            return { selectedIds, value: val, correct: computeCorrect(s, selectedIds) };
        }
        return null;
    };

    const handleSubmit = () => {
        // Already-answered question (review mode): navigate forward only, never re-submit.
        if (getReviewData(step)) {
            setNextstep(false);
            return;
        }

        let correct = false;
        if (step.slug === 'single-select' || step.slug === 'true-false') {
            const a = step.answers.find(x => x.qnsAnswerId === valueId);
            correct = a?.isCorrect || false;
        } else if (step.slug === 'multi-select') {
            const correctIds = step.answers.filter(x => x.isCorrect).map(x => x.qnsAnswerId);
            if (step.everyCorrectSelected === 'one') {
                correct = values.some(v => correctIds.includes(v));
            } else {
                correct = areEqual(values, correctIds);
            }
        }

        const model = {
            QNSAnswerId: valueId ? valueId : step.answers?.length > 0 ? step.answers[0].qnsAnswerId : null,
            UserCourseStepId: step.userCourseStepId,
            Value: value,
            Values: values,
            QNSID: step.qnsid,
            Slug: step.slug,
            UserCourseId: step.userCourseId,
            Correct: correct,
            AnsweredOn: moment().format('MM/DD/YYYY HH:mm:ss'),
        };

        axios.post(AUTH_CONFIG.API_URL + '/api/course/SetUserAnswer', model)
            .then((response) => {
                setScore(response.data.score);

                if (step.slug !== 'text' && step.slug !== 'video') {
                    const selectedIds = step.slug === 'multi-select' ? values : (valueId ? [valueId] : []);
                    const isChoice = step.slug === 'single-select' || step.slug === 'true-false' || step.slug === 'multi-select';
                    setAnsweredQuestions(prev => ({
                        ...prev,
                        [step.userCourseStepId]: {
                            selectedIds,
                            value: value != null ? value : null,
                            correct: isChoice ? correct : null,
                        }
                    }));
                    setJustAnsweredId(step.userCourseStepId);
                }

                if (step.slug === 'single-select' || step.slug === 'true-false' || step.slug === 'multi-select') {
                    showFeedback(correct, response.data.complete);
                } else {
                    setNextstep(response.data.complete);
                }
            });
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                <Spinner size={SpinnerSize.large} />
            </div>
        );
    }

    const pct = step.stepNumber ? step.stepNumber / steps.length : 0;
    const reviewData = !complete ? getReviewData(step) : null;

    return (
        <div className="box2 white-to-grey mt30" css={quizInner}>
            {/* Header with progress */}
            {!complete && step && (
                <div style={{ marginBottom: 20 }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {title}
                    </h3>
                    <div css={progressBarTrack}>
                        <div css={progressBarFill(pct)} />
                    </div>
                    <div css={stepCount}>{step.stepNumber}/{steps.length}</div>
                </div>
            )}

            {/* Question area with proper slide animation */}
            <div css={questionArea}>
                <AnimatePresence initial={false} custom={direction} mode="sync">
                    <motion.div
                        key={complete ? 'complete' : step.courseStepId}
                        custom={direction}
                        variants={slideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={slideTransition}
                        style={{ width: '100%' }}
                    >
                        {complete && (
                            <Complete steps={steps} data={step} score={score} back={onComplete} />
                        )}
                        {!complete && reviewData && (
                            <ReviewStep data={step} review={reviewData} />
                        )}
                        {!complete && !reviewData && step.slug === 'single-select' && (
                            <SingleSelect showExplanation={showExplanation} setShowExplanation={setShowExplanation} explanationIsCorrect={explanationCorrect} complete={complete} data={step} callback={setNextstep} value={valueId} showCorrect={showCorrect} showInCorrect={showInCorrect} setValueId={setValueId} />
                        )}
                        {!complete && !reviewData && step.slug === 'multi-select' && (
                            <MultipleSelect showExplanation={showExplanation} setShowExplanation={setShowExplanation} explanationIsCorrect={explanationCorrect} complete={complete} data={step} callback={setNextstep} value={values} showCorrect={showCorrect} showInCorrect={showInCorrect} setValues={setValues} />
                        )}
                        {!complete && !reviewData && step.slug === 'true-false' && (
                            <TrueFalse showExplanation={showExplanation} setShowExplanation={setShowExplanation} explanationIsCorrect={explanationCorrect} complete={complete} data={step} callback={setNextstep} value={valueId} showCorrect={showCorrect} showInCorrect={showInCorrect} setValueId={setValueId} />
                        )}
                        {!complete && !reviewData && step.slug === 'free-answer' && (
                            <FreeAnswer data={step} callback={setNextstep} value={value} setValue={setValue} />
                        )}
                        {!complete && !reviewData && step.slug === 'rating' && (
                            <StarRating data={step} callback={setNextstep} value={value} setValue={setValue} />
                        )}
                        {!complete && step.slug === 'text' && (
                            <Text data={step} callback={setNextstep} />
                        )}
                        {!complete && step.slug === 'video' && (
                            <Video data={step} callback={setNextstep} />
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Bottom navigation */}
            {!complete && !showExplanation && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '14px 0 0',
                    borderTop: '1px solid rgba(var(--palette-neutral-4), 1)',
                    marginTop: 'auto',
                }}>
                    <DefaultButton
                        text="Back"
                        className="mr10 btn-secondary-white"
                        disabled={step.stepNumber === 1}
                        onClick={setstepBack}
                    />
                    <span style={{ fontSize: 13, color: '#605e5c' }}>{step.stepNumber}/{steps.length}</span>
                    <PrimaryButton
                        className="btn-primary"
                        disabled={(!reviewData && !value && !valueId && values.length === 0 && stepDisabled && step.slug !== 'text') || showInCorrect || showCorrect}
                        onClick={handleSubmit}
                        text="Next"
                    />
                </div>
            )}
        </div>
    );
}

export default QuizzesPage;
